import { useState } from "react";
import type { FormEvent } from "react";
import { categories } from "@/mocks/homeData";

type Status = "idle" | "submitting" | "success" | "error";

export default function Subscribe() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，感谢你的关注，我们会把最新写真合集发送到你的邮箱。");
      form.reset();
      return;
    }

    setStatus("submitting");
    setMessage("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (value instanceof File) {
        payload.append(key, "Uncollectable");
      } else {
        payload.append(key, String(value));
      }
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/db547t6k6jkc2oct4big", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk) {
        setStatus("success");
        setMessage("订阅成功，感谢你的关注，我们会把最新写真合集发送到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg.length > 0
            ? serverMsg
            : "提交失败，请检查后重试。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <section
      id="subscribe"
      className="scroll-mt-24 bg-background-100 px-4 py-16 md:px-8 md:py-24"
    >
      <div className="mx-auto max-w-4xl overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-primary-500 p-8 md:p-10">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-50/20 text-background-50">
              <i className="ri-mail-send-line text-2xl" />
            </span>
            <h2 className="mt-5 font-heading text-2xl font-bold text-background-50 md:text-3xl">
              订阅写真更新
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-background-100">
              留下邮箱，我们会在每周整理好新上架的写真合集后发送提醒，包含当周新增的类别与拍摄花絮。
              不打扰、不刷屏，只为让你第一时间看到新内容。
            </p>
            <ul className="mt-6 space-y-2 text-sm text-background-100">
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-accent-300" />
                每周精选新合集汇总
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-accent-300" />
                可按偏好类别接收
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-accent-300" />
                随时可退订
              </li>
            </ul>
          </div>

          <div className="p-8 md:p-10">
            <form
              id="subscribe-form"
              data-readdy-form
              action="https://readdy.ai/api/form/db547t6k6jkc2oct4big"
              method="post"
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div>
                <label
                  htmlFor="subscribe-email"
                  className="mb-1.5 block text-sm font-medium text-foreground-800"
                >
                  邮箱地址
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div>
                <label
                  htmlFor="subscribe-interest"
                  className="mb-1.5 block text-sm font-medium text-foreground-800"
                >
                  偏好类别（可选）
                </label>
                <select
                  id="subscribe-interest"
                  name="interest"
                  defaultValue=""
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                >
                  <option value="">全部类别</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <input
                type="text"
                name="website_alt"
                className="field-extra-note"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                {status === "submitting" ? "提交中..." : "订阅更新"}
              </button>

              {message ? (
                <p
                  role="status"
                  className={`rounded-md px-4 py-3 text-sm ${
                    status === "success"
                      ? "bg-secondary-100 text-secondary-900"
                      : "bg-primary-100 text-primary-800"
                  }`}
                >
                  {message}
                </p>
              ) : null}

              <p className="text-xs leading-relaxed text-foreground-500">
                提交即表示你同意接收本站的写真更新提醒，我们仅使用该邮箱发送订阅内容。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}